import { Home, MessageSquare, BarChart3, LogOut, LayoutDashboard, Sun, Moon, Building2, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/Button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import AuthRedirect from "./AuthRedirect";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/DropdownMenu";
// Avatar components no longer needed

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [darkTheme, setDarkTheme] = useState(() => document.documentElement.classList.contains('dark'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const toggleTheme = () => {
    const currentlyDark = document.documentElement.classList.contains('dark');
    if (currentlyDark) {
      document.documentElement.classList.remove('dark');
      setDarkTheme(false);
      try { localStorage.setItem('theme', 'light'); } catch (_) {}
    } else {
      document.documentElement.classList.add('dark');
      setDarkTheme(true);
      try { localStorage.setItem('theme', 'dark'); } catch (_) {}
    }
  };

  useEffect(() => {
    // Mantener sincronizado el estado al montar
    setDarkTheme(document.documentElement.classList.contains('dark'));

    // Reaccionar a cambios del sistema si no hay preferencia guardada
    const hasStoredPref = (() => {
      try { return !!localStorage.getItem('theme'); } catch (_) { return false; }
    })();
    const mql = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
    if (!mql) return;
    const onChange = (e) => {
      if (hasStoredPref) return; // respetar preferencia del usuario si existe
      if (e.matches) {
        document.documentElement.classList.add('dark');
        setDarkTheme(true);
      } else {
        document.documentElement.classList.remove('dark');
        setDarkTheme(false);
      }
    };
    try {
      mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange);
    } catch (_) {}
    return () => {
      try {
        mql.removeEventListener ? mql.removeEventListener('change', onChange) : mql.removeListener(onChange);
      } catch (_) {}
    };
  }, []);
  
  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 flex items-center justify-center">
            <img 
              src="/gabi.png" 
              alt="GABI" 
              className="w-full h-full object-contain"
            />
          </div>
          <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
            GABI IA
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          <Link to="/">
            <Button
              variant={isActive("/") ? "secondary" : "ghost"}
              size="sm"
              className="gap-2"
            >
              <Home className="w-4 h-4" />
              Inicio
            </Button>
          </Link>
          {user && (
            <>
              <Link to="/opciones">
                <Button
                  variant={isActive("/opciones") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                >
                  <Building2 className="w-4 h-4" />
                  Opciones
                </Button>
              </Link>
              <Link to="/chat">
                <Button
                  variant={isActive("/chat") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Chat
                </Button>
              </Link>
              <Link to="/analyzer">
                <Button
                  variant={isActive("/analyzer") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                >
                  <BarChart3 className="w-4 h-4" />
                  Informes
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button
                  variant={isActive("/dashboard") ? "secondary" : "ghost"}
                  size="sm"
                  className="gap-2"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Button>
              </Link>
            </>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="p-1"
            onClick={toggleTheme}
            title={darkTheme ? 'Cambiar a claro' : 'Cambiar a oscuro'}
            aria-pressed={darkTheme}
          >
            <div className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${darkTheme ? 'bg-primary' : 'bg-secondary'}`}>
              {/* Iconos sol/luna en los extremos (por encima de la perilla) */}
              <Sun className={`pointer-events-none absolute z-20 left-1 top-1/2 -translate-y-1/2 w-3.5 h-3.5 transition-opacity duration-200 ${darkTheme ? 'opacity-0 text-white' : 'opacity-100 text-yellow-500'}`} />
              <Moon className={`pointer-events-none absolute z-20 right-1 top-1/2 -translate-y-1/2 w-3.5 h-3.5 transition-opacity duration-200 drop-shadow ${darkTheme ? 'opacity-100 text-yellow-300' : 'opacity-0 text-slate-400'}`} />
              {/* Perilla deslizante */}
              <span
                className={`absolute z-0 top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-300 ease-out ${darkTheme ? 'translate-x-6' : 'translate-x-0'}`}
              />
            </div>
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="p-1"
            onClick={toggleTheme}
            title={darkTheme ? 'Cambiar a claro' : 'Cambiar a oscuro'}
            aria-pressed={darkTheme}
          >
            <div className={`relative w-10 h-5 rounded-full transition-colors duration-200 ${darkTheme ? 'bg-primary' : 'bg-secondary'}`}>
              <Sun className={`pointer-events-none absolute z-20 left-0.5 top-1/2 -translate-y-1/2 w-3 h-3 transition-opacity duration-200 ${darkTheme ? 'opacity-0 text-white' : 'opacity-100 text-yellow-500'}`} />
              <Moon className={`pointer-events-none absolute z-20 right-0.5 top-1/2 -translate-y-1/2 w-3 h-3 transition-opacity duration-200 drop-shadow ${darkTheme ? 'opacity-100 text-yellow-300' : 'opacity-0 text-slate-400'}`} />
              <span
                className={`absolute z-0 top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transform transition-transform duration-300 ease-out ${darkTheme ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </div>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden"
            aria-label="Abrir menú"
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </Button>
        </div>

        {/* Desktop User Menu */}
        {user ? (
          <div className="hidden md:block">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
                {user.photoURL ? (
                  <img 
                    src={user.photoURL} 
                    alt={user.displayName || user.email}
                    className="w-10 h-10 object-cover rounded-full"
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                    {getUserInitials()}
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="flex items-center justify-start gap-2 p-2">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user.email}</p>
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard" className="flex items-center cursor-pointer">
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Dashboard
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    await logout();
                  } finally {
                    navigate("/auth", { replace: true });
                  }
                }}
                className="cursor-pointer"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        ) : (
          <div className="hidden md:block">
            <AuthRedirect>
              <Button variant="hero" size="sm">
                Pruébalo ya
              </Button>
            </AuthRedirect>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/70 z-40 md:hidden backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Menu Panel */}
          <div className="fixed top-16 left-0 right-0 z-50 md:hidden bg-card border-b border-border shadow-2xl">
            <div className="container mx-auto px-4 py-6 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                  isActive("/") 
                    ? "bg-primary/10 text-primary border border-primary/20" 
                    : "hover:bg-secondary/80 text-foreground"
                }`}
              >
                <Home className="w-5 h-5" />
                <span className="font-semibold">Inicio</span>
              </Link>
              
              {user ? (
                <>
                  <Link 
                    to="/opciones" 
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                      isActive("/opciones") 
                        ? "bg-primary/10 text-primary border border-primary/20" 
                        : "hover:bg-secondary/80 text-foreground"
                    }`}
                  >
                    <Building2 className="w-5 h-5" />
                    <span className="font-semibold">Opciones</span>
                  </Link>
                  
                  <Link 
                    to="/chat" 
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                      isActive("/chat") 
                        ? "bg-primary/10 text-primary border border-primary/20" 
                        : "hover:bg-secondary/80 text-foreground"
                    }`}
                  >
                    <MessageSquare className="w-5 h-5" />
                    <span className="font-semibold">Chat</span>
                  </Link>
                  
                  <Link 
                    to="/analyzer" 
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                      isActive("/analyzer") 
                        ? "bg-primary/10 text-primary border border-primary/20" 
                        : "hover:bg-secondary/80 text-foreground"
                    }`}
                  >
                    <BarChart3 className="w-5 h-5" />
                    <span className="font-semibold">Informes</span>
                  </Link>
                  
                  <Link 
                    to="/dashboard" 
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 p-4 rounded-xl transition-all ${
                      isActive("/dashboard") 
                        ? "bg-primary/10 text-primary border border-primary/20" 
                        : "hover:bg-secondary/80 text-foreground"
                    }`}
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    <span className="font-semibold">Dashboard</span>
                  </Link>
                  
                  <div className="border-t border-border my-4" />
                  
                  <div className="px-4 py-3 bg-secondary/50 rounded-xl mb-2">
                    <p className="text-xs text-muted-foreground mb-1">Sesión iniciada como</p>
                    <p className="text-sm font-medium text-foreground truncate">{user.email}</p>
                  </div>
                  
                  <button
                    onClick={async () => {
                      try {
                        await logout();
                      } finally {
                        setMobileMenuOpen(false);
                        navigate("/auth", { replace: true });
                      }
                    }}
                    className="w-full flex items-center gap-3 p-4 rounded-xl hover:bg-destructive/10 text-destructive transition-colors font-semibold"
                  >
                    <LogOut className="w-5 h-5" />
                    <span>Cerrar sesión</span>
                  </button>
                </>
              ) : (
                <div className="pt-2">
                  <AuthRedirect>
                    <Button 
                      variant="hero" 
                      size="sm" 
                      className="w-full"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Pruébalo ya
                    </Button>
                  </AuthRedirect>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </nav>
  );
};
